<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    ref,
  } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSegmented from '@/components/pos-v2/ui/PosUiSegmented.vue';
  import PosUiStepper from '@/components/pos-v2/ui/PosUiStepper.vue';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import { useModal } from '@/composition/use-modal';
  import {
    ADDON_GROUP_MODAL,
    GROUP_MODES,
    GROUP_OPTIONS_MAX,
    GROUP_TITLE_MAX,
    OPTION_NAME_MAX,
    PRICE_INPUT_MAX_LENGTH,
    addOptionRow,
    addonGroupForm,
    costlierOptionKeys,
    groupFormRules,
    groupLimitBounds,
    groupSaveOps,
    isGroupMode,
    limitedMax,
    readAddonGroupForm,
    removeOptionRow,
    setGroupMin,
    setGroupMode,
    setGroupRequired,
    updateOptionRow,
  } from '@/domain/menu-import/item-editor';
  import type {
    IAddonGroupForm,
    IOptionRowErrors,
    TGroupMode,
  } from '@/domain/menu-import/item-editor';
  import { addonRuleKey } from '@/domain/menu-import/menu-draft';
  import { currencySymbol } from '@/domain/store-setup/money';
  import type { IDraftAddonGroup, TDraftOp } from '@/interfaces/IMenuImport';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Grupo de agregados» (plan §4.5.2), a dynamic modal opened with
   * `showDynamicDialog<TDraftOp[] | null>(…, ADDON_GROUP_MODAL)`: the group's
   * name, how diners choose («Eligen una» / «Eligen varias» / «Extras») with
   * its minimum and maximum, the rule in plain words and the options with
   * their extra price. «Guardar» resolves the operations of one save (a new
   * group is also linked to the dish), «Quitar grupo» its removal; a group
   * shared by several dishes asks first. Closing resolves null.
   */
  const MODE_KEYS: Readonly<Record<TGroupMode, string>> = {
    pickOne: 'menuImport.group.pickOne',
    pickMany: 'menuImport.group.pickMany',
    extras: 'menuImport.group.extras',
  };

  const props = withDefaults(
    defineProps<{
      /** null: a new group for the dish. */
      group?: IDraftAddonGroup | null;
      itemId: string;
      /** Dishes that use the group now. */
      usedIn: number;
      /** The dish's price (or its cheapest size), for the «costs more» hint. */
      itemPrice?: number | null;
      currency: string;
      country: string;
    }>(),
    { group: null, itemPrice: null }
  );
  const emit = defineEmits<{ (e: 'close'): void }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const modals = useModal();

  const uid = nextDomId('menu-group');
  const titleId = `${uid}-title`;
  const howId = `${uid}-how`;
  const body = ref<HTMLElement | null>(null);
  const form = ref<IAddonGroupForm>(
    addonGroupForm(props.group, props.country, () =>
      nextDomId('menu-group-option')
    )
  );
  /** «Guardar» was tried: the errors show from then on. */
  const attempted = ref(false);
  let alive = true;

  const read = computed(() => readAddonGroupForm(form.value, props.country));
  const errors = computed(() => {
    const current = read.value;
    return attempted.value && current.status === 'invalid' ? current : null;
  });
  const shared = computed(() => props.usedIn >= 2);
  const symbol = computed(() => currencySymbol(props.currency));
  const bounds = computed(() => groupLimitBounds(form.value));
  const noLimit = computed(() => form.value.max === null);
  const modeItems = computed(() =>
    GROUP_MODES.map((mode) => ({ id: mode, label: tr(MODE_KEYS[mode]) }))
  );
  const ruleText = computed(() => {
    const rule = addonRuleKey(groupFormRules(form.value));
    return tr(rule.key, rule.params);
  });
  const costlier = computed(
    () =>
      new Set(costlierOptionKeys(form.value, props.itemPrice, props.country))
  );
  const canAddOption = computed(
    () => form.value.options.length < GROUP_OPTIONS_MAX
  );

  function optionNameId(key: string): string {
    return `${uid}-${key}-name`;
  }

  function optionError(key: string, field: keyof IOptionRowErrors): boolean {
    return errors.value?.options[key]?.[field] ?? false;
  }

  //#region Form
  function setTitle(text: string) {
    form.value = { ...form.value, title: text };
  }

  function setMode(id: string) {
    if (isGroupMode(id)) form.value = setGroupMode(form.value, id);
  }

  function setRequired(required: boolean) {
    form.value = setGroupRequired(form.value, required);
  }

  function setMin(min: number) {
    form.value = setGroupMin(form.value, min);
  }

  function setMax(max: number) {
    form.value = { ...form.value, max };
  }

  function toggleNoLimit() {
    form.value = {
      ...form.value,
      max: noLimit.value ? limitedMax(form.value) : null,
    };
  }

  function setOptionName(key: string, name: string) {
    form.value = updateOptionRow(form.value, key, { name });
  }

  function setOptionPrice(key: string, priceText: string) {
    form.value = updateOptionRow(form.value, key, { priceText });
  }

  async function focusOption(key: string) {
    await nextTick();
    if (!alive) return;
    const input = document.getElementById(optionNameId(key));
    if (input instanceof HTMLInputElement) input.focus();
  }

  function addOption() {
    const key = nextDomId('menu-group-option');
    form.value = addOptionRow(form.value, key);
    void focusOption(key);
  }

  function removeOption(key: string) {
    const index = form.value.options.findIndex((option) => option.key === key);
    form.value = removeOptionRow(form.value, key);
    const options = form.value.options;
    const neighbour = options[Math.min(index, options.length - 1)];
    if (neighbour) void focusOption(neighbour.key);
  }
  //#endregion

  //#region Save
  function submit(ops: TDraftOp[]) {
    void modals.dialogSubmit(ADDON_GROUP_MODAL, ops);
  }

  /** The first message under a field, brought into view after a failed «Guardar». */
  async function showFirstError() {
    await nextTick();
    if (!alive) return;
    const first = body.value?.querySelector('[role="alert"]');
    if (first instanceof HTMLElement)
      first.scrollIntoView({ block: 'nearest' });
  }

  async function confirmShared(acceptText: string, acceptColor?: string) {
    return modals.confirm({
      title: tr('menuImport.group.sharedConfirmTitle'),
      message: tr('menuImport.group.sharedConfirmMessage', {
        count: props.usedIn,
      }),
      acceptText,
      cancelText: tr('cancel'),
      acceptColor,
    });
  }

  async function save() {
    attempted.value = true;
    const result = read.value;
    if (result.status === 'invalid') {
      void showFirstError();
      return;
    }
    if (shared.value) {
      const confirmed = await confirmShared(
        tr('menuImport.group.sharedConfirmAccept')
      );
      if (!confirmed || !alive) return;
    }
    submit(groupSaveOps(props.group, result.group, props.itemId));
  }

  async function removeGroup() {
    const group = props.group;
    if (!group) return;
    if (shared.value) {
      const confirmed = await confirmShared(
        tr('menuImport.group.remove'),
        'error'
      );
      if (!confirmed || !alive) return;
    }
    submit([{ op: 'deleteAddonGroup', groupId: group.id }]);
  }
  //#endregion

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<template>
  <div class="v2-menu-addon-group-modal">
    <header class="modal-head">
      <h2 class="modal-title">{{ $t('menuImport.group.title') }}</h2>
      <button
        type="button"
        class="modal-close"
        :aria-label="$t('cancel')"
        @click="emit('close')"
      >
        <PosUiIcon name="close" :size="20" />
      </button>
    </header>
    <div ref="body" class="modal-body">
      <PosUiField
        :label="$t('menuImport.group.name')"
        :input-id="titleId"
        :error="
          errors && errors.title ? $t('menuImport.group.invalid') : undefined
        "
      >
        <PosUiInput
          :id="titleId"
          :value="form.title"
          :maxlength="GROUP_TITLE_MAX"
          :placeholder="$t('menuImport.group.namePlaceholder')"
          autocomplete="off"
          @input="setTitle"
        />
      </PosUiField>
      <p v-if="shared" class="used-in">
        <PosUiIcon name="link-variant" class="used-in-icon" />
        {{ $t('menuImport.group.usedIn', { count: usedIn }) }}
      </p>

      <section class="modal-block">
        <p :id="howId" class="block-label">{{ $t('menuImport.group.how') }}</p>
        <PosUiSegmented
          block
          :value="form.mode"
          :items="modeItems"
          :aria-labelledby="howId"
          @input="setMode"
        />
        <PosUiSwitch
          v-if="form.mode !== 'extras'"
          :value="form.required"
          :label="$t('menuImport.group.required')"
          @input="setRequired"
        />
        <div v-if="form.mode !== 'pickOne'" class="limits">
          <div v-if="form.mode === 'pickMany'" class="limit">
            <span class="limit-label">{{ $t('menuImport.group.min') }}</span>
            <PosUiStepper
              :value="form.min"
              :min="bounds.minLow"
              :max="bounds.minHigh"
              :aria-label="$t('menuImport.group.min')"
              @input="setMin"
            />
          </div>
          <div class="limit">
            <span class="limit-label">{{ $t('menuImport.group.max') }}</span>
            <div class="limit-controls">
              <PosUiStepper
                v-show="!noLimit"
                :value="form.max === null ? bounds.maxHigh : form.max"
                :min="bounds.maxLow"
                :max="bounds.maxHigh"
                :aria-label="$t('menuImport.group.max')"
                @input="setMax"
              />
              <PosUiChip :active="noLimit" @click="toggleNoLimit">{{
                $t('menuImport.group.noLimit')
              }}</PosUiChip>
            </div>
          </div>
        </div>
        <p class="rule-preview">
          <PosUiIcon name="information-outline" class="rule-icon" />
          <span class="rule-text">{{ ruleText }}</span>
        </p>
        <p v-if="errors && errors.rules" class="form-error" role="alert">
          {{ $t('menuImport.group.minMax') }}
        </p>
      </section>

      <section class="modal-block">
        <div class="options-head" aria-hidden="true">
          <span class="head-name">{{ $t('menuImport.group.option') }}</span>
          <span class="head-price">{{
            $t('menuImport.group.extraPrice')
          }}</span>
        </div>
        <div
          v-for="option in form.options"
          :key="option.key"
          class="option-row"
        >
          <div class="option-inputs">
            <PosUiInput
              :id="optionNameId(option.key)"
              class="option-name"
              :value="option.name"
              :maxlength="OPTION_NAME_MAX"
              :placeholder="$t('menuImport.group.option')"
              :aria-label="$t('menuImport.group.option')"
              autocomplete="off"
              @input="setOptionName(option.key, $event)"
            />
            <PosUiInput
              class="option-price"
              :value="option.priceText"
              :prefix="symbol"
              :maxlength="PRICE_INPUT_MAX_LENGTH"
              placeholder="0"
              :aria-label="$t('menuImport.group.extraPrice')"
              inputmode="decimal"
              autocomplete="off"
              @input="setOptionPrice(option.key, $event)"
            />
            <button
              type="button"
              class="option-remove"
              :aria-label="$t('menuImport.group.removeOption')"
              :title="$t('menuImport.group.removeOption')"
              :disabled="form.options.length <= 1"
              @click="removeOption(option.key)"
            >
              <PosUiIcon name="close" :size="18" />
            </button>
          </div>
          <p
            v-if="optionError(option.key, 'name')"
            class="form-error"
            role="alert"
          >
            {{ $t('menuImport.editor.nameRequired') }}
          </p>
          <p
            v-else-if="optionError(option.key, 'price')"
            class="form-error"
            role="alert"
          >
            {{ $t('menuImport.editor.priceInvalid') }}
          </p>
          <p v-if="costlier.has(option.key)" class="option-warning">
            <PosUiIcon name="alert-outline" class="warning-icon" />
            {{ $t('menuImport.group.costlier') }}
          </p>
        </div>
        <button
          v-if="canAddOption"
          type="button"
          class="modal-link"
          @click="addOption"
        >
          <PosUiIcon name="plus" class="link-icon" />
          {{ $t('menuImport.group.addOption') }}
        </button>
      </section>
    </div>
    <footer class="modal-foot">
      <PosUiButton
        v-if="group"
        class="foot-remove"
        color="error"
        variant="ghost"
        icon="delete-outline"
        @click="removeGroup"
        >{{ $t('menuImport.group.remove') }}</PosUiButton
      >
      <div class="foot-main">
        <PosUiButton color="neutral" variant="outline" @click="emit('close')">{{
          $t('cancel')
        }}</PosUiButton>
        <PosUiButton @click="save">{{
          $t('menuImport.group.save')
        }}</PosUiButton>
      </div>
    </footer>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-addon-group-modal {
    @apply tw-flex tw-flex-col tw-bg-white;
    max-height: 85vh;

    .modal-head {
      @apply tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-py-3 tw-pl-5 tw-pr-3;
    }
    .modal-title {
      @apply tw-m-0 tw-text-lg tw-font-semibold tw-text-neutral-900;
    }
    .modal-close {
      @apply tw-flex tw-h-11 tw-w-11 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-neutral-500 hover:tw-bg-neutral-100 hover:tw-text-neutral-800;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .modal-body {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-5 tw-overflow-y-auto tw-px-5 tw-py-4;
      overscroll-behavior: contain;
    }
    .used-in {
      @apply -tw-mt-3 tw-mb-0 tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-neutral-600;
      .used-in-icon {
        @apply tw-text-base tw-text-neutral-500;
      }
    }
    .modal-block {
      @apply tw-flex tw-flex-col tw-gap-3;
    }
    .block-label {
      @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .limits {
      @apply tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-3;
    }
    .limit {
      @apply tw-flex tw-flex-col tw-gap-1.5;
    }
    .limit-label {
      @apply tw-text-sm tw-text-neutral-700;
    }
    .limit-controls {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;
    }
    .rule-preview {
      @apply tw-m-0 tw-flex tw-items-center tw-gap-2 tw-self-start tw-rounded-lg tw-bg-neutral-100 tw-px-3 tw-py-2 tw-text-sm tw-text-neutral-700;
      .rule-icon {
        @apply tw-shrink-0 tw-text-base tw-text-neutral-500;
      }
      .rule-text {
        &::first-letter {
          @apply tw-uppercase;
        }
      }
    }
    .options-head {
      @apply tw-flex tw-items-center tw-gap-2 tw-pr-[52px] tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-500;
      .head-name {
        @apply tw-min-w-0 tw-flex-1;
      }
      .head-price {
        @apply tw-w-32 tw-shrink-0;
      }
    }
    .option-row {
      @apply tw-flex tw-flex-col tw-gap-1;
    }
    .option-inputs {
      @apply tw-flex tw-items-center tw-gap-2;
    }
    .option-name {
      @apply tw-min-w-0 tw-flex-1;
    }
    .option-price {
      @apply tw-w-32 tw-shrink-0;
    }
    .option-remove {
      @apply tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-neutral-500 hover:tw-bg-neutral-100 hover:tw-text-neutral-800;
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-300 hover:tw-bg-transparent;
      }
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .option-warning {
      @apply tw-m-0 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-amber-700;
      .warning-icon {
        @apply tw-shrink-0 tw-text-sm;
      }
    }
    .form-error {
      @apply tw-m-0 tw-text-xs tw-text-red-600;
    }
    .modal-link {
      @apply tw-inline-flex tw-min-h-11 tw-cursor-pointer tw-items-center tw-gap-1 tw-self-start tw-border-0 tw-bg-transparent tw-px-0 tw-text-sm tw-font-medium tw-text-neutral-700 hover:tw-text-neutral-900;
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
      .link-icon {
        @apply tw-text-base;
      }
    }
    .modal-foot {
      @apply tw-flex tw-shrink-0 tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-px-5 tw-py-3;
    }
    .foot-main {
      @apply tw-ml-auto tw-flex tw-gap-2;
    }
  }
</style>
